import  { useState, useEffect } from "react";
import {
  Table,
  Card,
  Button,
  Modal,
  Form,
  Input,
  message,
  Spin,
} from "antd";
import { PlusOutlined, SearchOutlined, CloseOutlined } from "@ant-design/icons";
import useApis from "../apis/useApis";
import appConstant from "./../json/appConstant.json";

const Constructions = ({ isDarkMode }) => {
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [constructionData, setConstructionData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [loading, setLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const { jsonApi } = useApis();

  useEffect(() => {
    fetchConstructions();
  }, [pagination.current, pagination.pageSize]);

  const fetchConstructions = async () => {
    setIsDataLoading(true);
    const params = {
      page: pagination.current,
      pageSize: pagination.pageSize,
      search: searchText,
    };

    const response = await jsonApi(appConstant.Construction, params, {}, null);

    if (response?.data?.code === 200) {
      const list = response.data.data.map((item) => ({
        ...item,
        key: item.ConstNatureID,
      }));

      setConstructionData(list);
      setPagination((prev) => ({
        ...prev,
        total: response.data.total || list.length,
      }));
    }

    setIsDataLoading(false);
  };

  const showModal = () => {
    setModalMode("add");
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await jsonApi(
        appConstant.createConstruction,
        null,
        values,
        null
      );

      if (response?.data?.code === 200) {
        message.success("Construction added successfully");
        fetchConstructions();
        handleCancel();
      } else {
        message.error(response?.data?.message || "Failed to add construction");
      }
    } catch (error) {
      console.error("Error:", error);
      message.error("An error occurred while adding construction");
    }
    setLoading(false);
  };

  const columns = [
    {
      title: "S.No",
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
      width: "5%",
    },
    {
      title: "Construction Nature (EN)",
      dataIndex: "ConstructionNature",
      key: "ConstructionNature",
    },
    {
      title: "Construction Nature (HI)",
      dataIndex: "ConstructionNatureH",
      key: "ConstructionNatureH",
    },
    {
      title: "PTypeID",
      dataIndex: "PTypeID",
      key: "PTypeID",
    },
  ];

  const renderModalContent = () => (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      <Form.Item
        name="ConstructionNature"
        label="Construction Nature (EN)"
        rules={[{ required: true, message: "Please input construction nature in English" }, { pattern: /^[A-Za-z\s]+$/, message: "Construction Nature can only contain letters and spaces" }]}
      >
        <Input placeholder="Enter Construction Nature in English" maxLength={50} />
      </Form.Item>

      <Form.Item
        name="ConstructionNatureH"
        label="Construction Nature (HI)"
        rules={[{ required: true, message: "Please input construction nature in Hindi" }]}
      >
        <Input placeholder="Enter Construction Nature in Hindi" maxLength={50} />
      </Form.Item>

      <Form.Item
        name="PTypeID"
        label="PType ID"
        rules={[{ required: true, message: "Please input PTypeID" }]}
      >
        <Input type="number" placeholder="Enter PTypeID" maxLength={50} />
      </Form.Item>
    </Form>
  );

  // Filter construction data based on search input
  const filteredData = constructionData.filter((item) => {
    const nature = item.ConstructionNature?.toLowerCase() || "";
    const natureH = item.ConstructionNatureH?.toLowerCase() || "";
    const ptype = item.PTypeID?.toString() || "";
    return (
      nature.includes(searchText.toLowerCase()) ||
      natureH.includes(searchText.toLowerCase()) ||
      ptype.includes(searchText)
    );
  });

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <h2 style={{ color: isDarkMode ? "#fff" : "#000" }}>Constructions</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={showModal}>
          Add Construction
        </Button>
      </div>

      <Input
        placeholder="Search constructions..."
        prefix={<SearchOutlined />}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        suffix={searchText && (
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={() => setSearchText("")}
          />
        )}
        style={{ width: 300, marginBottom: 16 }}
      />

      <Card>
        <Spin spinning={isDataLoading}>
          <Table
            columns={columns}
            dataSource={filteredData}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              onChange: (page, pageSize) => {
                setPagination({ current: page, pageSize });
              },
            }}
          />
        </Spin>
      </Card>

      <Modal
        title="Add Construction"
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" loading={loading} onClick={() => form.submit()}>
            Add
          </Button>,
        ]}
        width={600}
      >
        {renderModalContent()}
      </Modal>
    </div>
  );
};

export default Constructions;
